import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios'
// import { AuthResponseType } from '../types'
import { toast } from 'react-toastify'
import { AuthReply } from '@synapex/shared2/gateway-rest-api'

export const API_URL = import.meta.env.VITE_API_URL

class ApiInstance {
    private axios: AxiosInstance
    private authAxios: AxiosInstance

    constructor() {
        this.axios = axios.create({
            baseURL: API_URL,
            timeout: 120000,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        this.authAxios = axios.create({
            baseURL: API_URL,
            timeout: 120000,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        this.setupInterceptors()
    }

    private setupInterceptors() {
        this.axios.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('accessToken')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                } else {
                    console.error(`${config.url}: no token in localStorage`)
                }
                this.applyRequestId(config)
                return config
            },
            (error) => {
                console.error(`interceptors.request`)
                console.error(error)

                Promise.reject(error)
            }
        )

        this.authAxios.interceptors.request.use(
            (config) => {
                this.applyRequestId(config)
                return config
            },
            (error) => {
                console.error('authAxios request error')
                console.error(error)
                return Promise.reject(error)
            }
        )

        this.axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                console.error(`interceptors.response`)
                console.error(error)

                if (error.response.status === 413) {
                    toast.error('Превышен размер файла')
                }

                const originalRequest = error.config
                if (error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true
                    const refreshToken = localStorage.getItem('refreshToken')
                    if (refreshToken) {
                        try {
                            const { accessToken } =
                                await this.refreshAccessToken(refreshToken)
                            localStorage.setItem('accessToken', accessToken)
                            originalRequest.headers.Authorization = `Bearer ${accessToken}`
                            return this.axios(originalRequest)
                        } catch (refreshError) {
                            this.handleAuthError()
                            return Promise.reject(refreshError)
                        }
                    } else {
                        this.handleAuthError()
                        return Promise.reject(error)
                    }
                }
                return Promise.reject(error)
            }
        )
    }

    private async refreshAccessToken(
        refreshToken: string
    ): Promise<{ accessToken: string }> {
        const response = await this.axios.post('/refresh-token', {
            refreshToken,
        })
        return response.data
    }

    private handleAuthError() {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
    }

    async authenticate(
        token: string
    ): Promise<AuthReply | { message: string; error: string }> {
        const response = await this.authAxios.get<AuthReply>('/auth/verify', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })

        if (!response.data.accessToken) {
            return {
                message: 'Authentication failed',
                error: JSON.stringify(response.data),
            }
        }

        localStorage.setItem('accessToken', response.data.accessToken)
        localStorage.setItem('refreshToken', response.data.refreshToken)

        return response.data
    }

    async get<T>(
        endpoint: string,
        options: AxiosRequestConfig = {}
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.axios.get(
            endpoint,
            options
        )
        return response.data
    }

    async post<T>(
        endpoint: string,
        data: any,
        options: AxiosRequestConfig = {}
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.axios.post(
            endpoint,
            data,
            options
        )
        return response.data
    }

    async put<T>(
        endpoint: string,
        data: any,
        options: AxiosRequestConfig = {}
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.axios.put(
            endpoint,
            data,
            options
        )
        return response.data
    }

    async patch<T>(
        endpoint: string,
        data: any,
        options: AxiosRequestConfig = {}
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.axios.patch(
            endpoint,
            data,
            options
        )
        return response.data
    }

    async delete<T>(
        endpoint: string,
        options: AxiosRequestConfig = {}
    ): Promise<T> {
        const response: AxiosResponse<T> = await this.axios.delete(
            endpoint,
            options
        )
        return response.data
    }

    private applyRequestId(config: AxiosRequestConfig) {
        if (!config.headers) {
            config.headers = {}
        }

        const headerKey = 'x-request-id'
        const existing =
            (config.headers[headerKey] as string | undefined) ||
            (config.headers[headerKey.toUpperCase()] as string | undefined)

        if (!existing) {
            config.headers[headerKey] = generateRequestId()
        }
    }
}

export const api = new ApiInstance()

export type GatewayError = AxiosError<{
    message?: string
}>

export function isGatewayError(error: Error): error is GatewayError {
    return error instanceof AxiosError
}

function generateRequestId(): string {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID()
    }

    return Math.random().toString(36).slice(2, 10)
}
